import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { TradingViewChart } from '@/components/ui/trading-view-chart';
import { apiService } from '@/apiService';
import { BarData, ChartIndicatorData, IndicatorValue } from '@/types';
import signalRService, { BarState } from '@/services/signalRService';
import { fetchBarHistory, fetchOlderBars, mergeBars, parseTimeframe, barMs, HISTORY_TOP_UP_BELOW } from './chartHistory';
import {
  Settings,
  TrendingUp,
  Eye,
  EyeOff,
  X
} from 'lucide-react';

interface AppliedIndicator {
  name: string;
  type: 'sma' | 'ema' | 'rsi' | 'macd' | 'bollinger';
  parameters: { [key: string]: any };
  color: string;
}

interface ModernChartProps {
  symbol?: string;
  timeframe?: string;
  isPopout?: boolean;
  onClose?: () => void;
  className?: string;
  /** Height in px of the candlestick area. The chart container takes this as a
   *  fixed inline height, so a caller embedding the chart in a bounded box
   *  (the symbol drawer) must pass a height that fits or the canvas overflows
   *  its parent. Default matches the full-page dashboard chart. */
  chartHeight?: number;
}

// Convert BarState to BarData
const convertBarStateToBarData = (barState: BarState): BarData => {
  return {
    symbol: barState.symbol || '',
    openBidPrice: barState.openBidPrice || 0,
    openAskPrice: barState.openAskPrice || 0,
    closeBidPrice: barState.closeBidPrice || 0,
    closeAskPrice: barState.closeAskPrice || 0,
    highBidPrice: barState.highBidPrice || 0,
    highAskPrice: barState.highAskPrice || 0,
    lowBidPrice: barState.lowBidPrice || 0,
    lowAskPrice: barState.lowAskPrice || 0,
    volume: barState.volume || 0,
    vwap: barState.vwap,
    tradeCount: barState.tradeCount,
    barStartTime: barState.barStartTime || '',
    barEndTime: barState.barEndTime || '',
    opened: barState.opened || false,
    closed: barState.closed || false,
    isFinal: barState.closed || false,
    open: barState.open,
    high: barState.high,
    low: barState.low,
    close: barState.close,
  };
};

// Handle bar updates with proper state management
const handleBarUpdate = (bars: BarData[], newBar: BarState): BarData[] => {
  const convertedBar: BarData = convertBarStateToBarData(newBar);
  const barTimestamp = new Date(newBar.barStartTime).getTime();

  if (isNaN(barTimestamp) || barTimestamp <= 0) {
    console.warn(`Invalid bar timestamp received: ${newBar.barStartTime}`);
    return bars;
  }

  const existingIndex = bars.findIndex(b =>
    new Date(b.barStartTime).getTime() === barTimestamp
  );

  if (existingIndex >= 0) {
    const existing = bars[existingIndex];

    if (existing.isFinal && !convertedBar.isFinal) {
      console.debug(`Ignoring in-progress update for finalized bar at ${newBar.barStartTime}`);
      return bars;
    }

    const updatedBars = [...bars];
    updatedBars[existingIndex] = convertedBar;
    console.log(`[handleBarUpdate] Updated existing bar at index ${existingIndex}, isFinal: ${convertedBar.isFinal}, OHLC: O=${convertedBar.open} H=${convertedBar.high} L=${convertedBar.low} C=${convertedBar.close}`);
    return updatedBars.sort((a, b) =>
      new Date(a.barStartTime).getTime() - new Date(b.barStartTime).getTime()
    );
  } else {
    const updatedBars = [...bars, convertedBar];
    console.log(`[handleBarUpdate] Added new bar, total bars: ${updatedBars.length}`);

    if (updatedBars.length > 200) {
      updatedBars.shift();
    }

    return updatedBars.sort((a, b) =>
      new Date(a.barStartTime).getTime() - new Date(b.barStartTime).getTime()
    );
  }
};

/**
 * A crosshair timestamp of constant width.
 *
 * toLocaleString() was used here, and its width changes with the value it renders
 * ("8/26/2026, 3:46:11 PM" against "12/1/2026, 11:06:01 AM"). The readout sits in the
 * header's flex row, so that width changed on every mouse move and reflowed the whole
 * row underneath the cursor. Combined with tabular-nums this format is the same width
 * for every instant in the year.
 */
const crosshairStamp = (ms: number): string => {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const ModernChart: React.FC<ModernChartProps> = ({
  symbol: propSymbol = 'AAPL',
  timeframe: propTimeframe = '1M',
  isPopout = false,
  onClose,
  className,
  chartHeight = 400
}) => {
  const componentId = React.useRef<string>();
  // Guards the async history top-up: if the user switches symbol or timeframe
  // while a top-up is in flight, the stale response must not repaint the chart.
  const loadSeq = React.useRef(0);
  // Mirrors `bars` for the paging loader — reading state directly would rebuild
  // the callback (and re-attach the chart listener) on every incoming tick.
  const barsRef = React.useRef<BarData[]>([]);
  // Which (symbol, timeframe) the bars in barsRef actually belong to. Without this the paging
  // loader can run after the identity changes but before the replacement data lands, read the
  // PREVIOUS series' oldest timestamp, and — because its sequence is captured after the change —
  // sail past the sequence guard and merge a page from the wrong historical window.
  const barsIdentityRef = React.useRef<string>('');
  if (!componentId.current) {
    componentId.current = `${propSymbol}_${propTimeframe}_${Math.random().toString(36).substr(2, 9)}`;
    console.log(`[ModernChart] Component ${componentId.current} mounted`);
  }
  const [symbol, setSymbol] = useState(propSymbol);
  const [timeframe, setTimeframe] = useState(propTimeframe);
  const [bars, setBars] = useState<BarData[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');
  const [showVolume, setShowVolume] = useState(false);
  const [indicators, setIndicators] = useState<AppliedIndicator[]>([]);
  const [crosshairData, setCrosshairData] = useState<{
    time?: number;
    price?: number;
  }>({});
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [serverIndicators, setServerIndicators] = useState<Record<string, ChartIndicatorData>>({});

  React.useEffect(() => {
    barsRef.current = bars;
    barsIdentityRef.current = `${symbol}_${timeframe}`;
  }, [bars, symbol, timeframe]);

  const timeframes = [
    { value: '1M', label: '1m' },
    { value: '5M', label: '5m' },
    { value: '15M', label: '15m' },
    { value: '30M', label: '30m' },
    { value: '1H', label: '1H' },
    { value: '4H', label: '4H' },
    { value: '1D', label: '1D' },
    { value: '1W', label: '1W' },
  ];

  const availableIndicators = [
    { type: 'sma', name: 'SMA', defaultParams: { period: 20 } },
    { type: 'ema', name: 'EMA', defaultParams: { period: 20 } },
    { type: 'rsi', name: 'RSI', defaultParams: { period: 14 } },
    { type: 'macd', name: 'MACD', defaultParams: { fast: 12, slow: 26, signal: 9 } },
    { type: 'bollinger', name: 'Bollinger Bands', defaultParams: { period: 20, stdDev: 2 } },
  ];

  // Pan-back paging: fetch the page immediately older than what we hold and
  // prepend it. Resolving with 0 latches the chart's loader off, so an empty
  // window must mean "genuinely no more history" — hence the exact-window query
  // rather than a heuristic.
  const loadOlderBars = useCallback(async (): Promise<number> => {
    // The anchor is only meaningful if the bars belong to THIS series. -1 keeps paging armed:
    // the replacement data is still on its way.
    if (barsIdentityRef.current !== `${symbol}_${timeframe}`) return -1;

    const current = barsRef.current;
    // -1, not 0: "the series hasn't loaded yet" is not "there is no more
    // history". Returning 0 here would latch paging off before it ever ran —
    // the first range check fires while the initial fetch is still in flight.
    if (!current || current.length === 0) return -1;
    const oldest = new Date(current[0].barStartTime);
    if (isNaN(oldest.getTime())) return -1;

    const seq = loadSeq.current;
    const older = await fetchOlderBars(symbol, timeframe, oldest, 200);
    // Symbol/timeframe changed mid-flight — drop the page rather than splicing
    // one series' history into another's.
    if (seq !== loadSeq.current) return -1;   // superseded, not exhausted
    if (older.length === 0) return 0;

    let added = 0;
    setBars(prev => {
      const merged = mergeBars(older, prev);
      added = merged.length - prev.length;
      return merged;
    });
    return added;
  }, [symbol, timeframe]);

  // Initialize chart data
  const initializeChart = useCallback(async () => {
    // Bumped for EVERY load, not just ones that need history: if the timeframe
    // changes while a top-up is in flight and the new timeframe returns a full
    // series, an unbumped sequence would let the stale response through and
    // overwrite it.
    const seq = ++loadSeq.current;
    try {
      setError(null);
      console.log(`[ModernChart EVENT] Fetching initial chart data`, {
        triggeredBy: 'initializeChart callback',
        symbol, timeframe, timestamp: new Date().toISOString(),
        componentId: componentId.current
      });
      const response = await apiService.getChartData(symbol, timeframe, 100);

      // /api/Chart serves the Orleans BarsGrain, which exists only once a symbol has
      // been subscribed AND has built its first bar. Clicking into a symbol nobody is
      // watching therefore 404s here, and that is a normal cold start, not an error.
      //
      // This used to gate the history top-up below on a SUCCESSFUL response, so the
      // 404 fell through to setBars([]) and no history was ever requested. The chart
      // then sat on its spinner until the first live tick arrived, and only once a bar
      // existed did the paging path notice the series was short and fetch the history
      // that had been sitting in TimescaleDB — milliseconds away — the whole time. On
      // an illiquid symbol that first tick is 20-30 seconds, which is exactly how long
      // the chart appeared to hang, and why one lone candle showed up before the rest.
      //
      // History is now fetched whether or not a live series exists.
      const liveBars = (response.success && response.data?.bars) ? response.data.bars : [];
      console.log(`[ModernChart EVENT] Initial data received`, {
        barCount: liveBars.length, symbol, timeframe, liveSeries: response.success
      });

      if (response.success && response.data?.indicators) {
        setServerIndicators(response.data.indicators);
      }

      // Painted first so a live bar shows immediately when there is one. When there is
      // none, the spinner stays up rather than flashing an empty chart.
      if (liveBars.length > 0) {
        setBars(liveBars);
      }

      {
        if (liveBars.length < HISTORY_TOP_UP_BELOW) {
          // Anchor on the newest bar we actually hold. A live chart's newest bar
          // is ~now, but a chart parked on a past window must not have today's
          // candles appended to it.
          // Anchor on the END of the newest bucket, not its start. On a derived timeframe the
          // newest live bar is stamped with its BUCKET start (a 4H chart opened at 11:00 shows an
          // 08:00 candle holding minutes of ticks), so anchoring at 08:00 asked for bars ending
          // there and excluded the bucket's own persisted 08:01-10:59 minutes entirely. Reaching
          // through the bucket pulls that prefix in; mergeBars then combines it with the live tail
          // instead of letting the thin live candle stand alone.
          const newestStart = liveBars.length
            ? new Date(liveBars[liveBars.length - 1].barStartTime)
            : undefined;
          const tfForAnchor = parseTimeframe(timeframe);
          const anchor = newestStart && !isNaN(newestStart.getTime())
            ? new Date(newestStart.getTime() + (tfForAnchor ? barMs(tfForAnchor) : 0))
            : undefined;
          const history = await fetchBarHistory(symbol, timeframe, 200, anchor);
          // Dropped if the user changed symbol/timeframe while this was in flight.
          if (seq === loadSeq.current) {
            if (history.length > 0) {
              console.log(`[ModernChart EVENT] History top-up`, {
                historyBars: history.length, liveBars: liveBars.length, symbol, timeframe
              });
              // Functional update: a SignalR bar update may have landed while the
              // fetch was in flight, and merging into the captured `liveBars`
              // snapshot would roll that candle back.
              setBars(prev => mergeBars(history, prev.length ? prev : liveBars));
            } else if (liveBars.length === 0) {
              // Nothing live and nothing stored: a genuinely empty chart. Clearing
              // here (rather than before the fetch) is what keeps the spinner up
              // while history is on its way instead of flashing "no data".
              setBars([]);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error loading chart data:', error);
      setError(`Failed to load chart data: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setBars([]);
    }
  }, [symbol, timeframe]);

  // Setup SignalR connection
  useEffect(() => {
    const setupSignalR = async () => {
      try {
        await signalRService.ensureStarted();
        setConnectionStatus('Connected');

        await signalRService.subscribeToSymbol(symbol);

        const chartId = `${symbol}_${timeframe}`;
        await signalRService.subscribeToChart(chartId);

        const listenerId = componentId.current!;

        signalRService.addBarUpdateListener(listenerId, (receivedSymbol: string, barState: BarState) => {
          if (receivedSymbol === symbol) {
            console.log(`[ModernChart EVENT] Bar update received for ${receivedSymbol}`, {
              time: barState.barStartTime, isFinal: barState.isFinal
            });

            setBars(prevBars => {
              const updated = handleBarUpdate(prevBars, barState);
              return updated;
            });
          }
        });

        signalRService.setCallbacks({
          onChartUpdated: (receivedChartId: string, chart: any) => {
            if (receivedChartId === chartId && chart.bars) {
              setBars(chart.bars);
            }
          }
        });

        // Listen for real-time indicator updates from backend
        signalRService.addIndicatorUpdateListener(listenerId, (receivedChartId: string, indicatorName: string, values: IndicatorValue[]) => {
          if (receivedChartId === chartId) {
            setServerIndicators(prev => ({
              ...prev,
              [indicatorName]: {
                name: indicatorName,
                values,
                lastUpdate: new Date().toISOString(),
              }
            }));
          }
        });

      } catch (error) {
        console.error('SignalR connection failed:', error);
        setConnectionStatus('Connection Failed');
      }
    };

    setupSignalR();

    return () => {
      const chartId = `${symbol}_${timeframe}`;
      signalRService.unsubscribeFromChart(chartId).catch(console.error);
      signalRService.unsubscribeFromSymbol(symbol).catch(console.error);
      if (componentId.current) {
        signalRService.removeBarUpdateListener(componentId.current);
        signalRService.removeIndicatorUpdateListener(componentId.current);
      }
    };
  }, [symbol, timeframe]);

  // Load initial chart data
  useEffect(() => {
    initializeChart();
  }, [initializeChart]);

  // Subscribe to symbol updates with specific timeframe
  useEffect(() => {
    const subscribeToSymbol = async () => {
      try {
        await apiService.subscribeToSymbol(symbol, timeframe);
      } catch (error: any) {
        if (!error.message?.includes('404')) {
          console.error(`Failed to subscribe to ${symbol} for ${timeframe}:`, error);
        }
      }
    };
    subscribeToSymbol();
  }, [symbol, timeframe]);

  const handleCrosshairMove = (time: any, price: number | undefined) => {
    setCrosshairData({ time: time ? time * 1000 : undefined, price });
  };

  const addIndicator = (type: AppliedIndicator['type'], parameters: any) => {
    const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#f9ca24', '#f0932b'];
    const color = colors[indicators.length % colors.length];
    setIndicators(prev => [...prev, { name: type.toUpperCase(), type, parameters, color }]);
  };

  const handleTimeframeChange = (newTimeframe: string) => {
    setTimeframe(newTimeframe);
  };

  const removeIndicator = (index: number) => {
    setIndicators(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <Card className={`border ${className}`}>
      <CardHeader className="py-2 px-4">
        {/* min-w-0 + overflow-hidden are load-bearing, not cosmetic. Without them a
            wide header item pushes this row past the card, a horizontal scrollbar
            appears, that narrows the chart's w-full container, the ResizeObserver
            resizes the canvas, and the scrollbar goes away again - an oscillation
            that reads as the chart flickering and resizing under the cursor. */}
        <div className="flex items-center justify-between gap-2 min-w-0 overflow-hidden">
          <div className="flex items-center gap-3 min-w-0">
            {/* Symbol + Timeframe */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{symbol}</span>
              <span className="text-xs text-muted-foreground">/</span>
            </div>

            {/* Timeframe Buttons */}
            <div className="flex items-center gap-0.5">
              {timeframes.map((tf) => (
                <button
                  key={tf.value}
                  onClick={() => handleTimeframeChange(tf.value)}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    timeframe === tf.value
                      ? 'bg-primary/15 text-primary font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                >
                  {tf.label}
                </button>
              ))}
            </div>

            {/* Connection Status */}
            <div className="flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${
                connectionStatus === 'Connected' ? 'bg-emerald-500' : 'bg-red-500'
              }`} />
              <span className="text-[11px] text-muted-foreground">{connectionStatus}</span>
            </div>

            {/* Crosshair readout.

                Always rendered, fixed width. It used to be mounted only while the
                cursor was over a bar, so hovering inserted an element into this row
                and moving the cursor resized it - reflowing the header on every
                mouse event. Reserving the space means the layout is identical
                whether or not the crosshair is over anything. */}
            <div
              className="w-[132px] shrink-0 text-[11px] text-muted-foreground font-mono
                         tabular-nums whitespace-nowrap overflow-hidden"
              aria-live="off"
            >
              {crosshairData.time && crosshairData.price !== undefined
                ? `${crosshairStamp(crosshairData.time)}  ${crosshairData.price.toFixed(2)}`
                : ''}
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {/* Reserved for the same reason as the crosshair readout: this grows a
                digit at a time as live bars arrive, and each change shifted every
                control to its right. */}
            <span className="w-[52px] mr-2 text-right text-[10px] text-muted-foreground tabular-nums">
              {bars.length > 0 ? `${bars.length} bars` : ''}
            </span>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowVolume(!showVolume)}
              className="h-7 w-7"
              title={showVolume ? 'Hide volume' : 'Show volume'}
            >
              {showVolume ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Add indicator">
                  <TrendingUp className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {availableIndicators.map((indicator) => (
                  <DropdownMenuItem
                    key={indicator.type}
                    onClick={() => addIndicator(indicator.type as AppliedIndicator['type'], indicator.defaultParams)}
                  >
                    {indicator.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
              <DialogTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Chart settings">
                  <Settings className="h-3.5 w-3.5" />
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>Chart Settings</DialogTitle>
                  <DialogDescription>
                    Configure chart display options and select the symbol to track.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <label htmlFor="symbol" className="text-right text-sm font-medium">
                      Symbol
                    </label>
                    <div className="col-span-3">
                      <Select value={symbol} onValueChange={setSymbol}>
                        <SelectTrigger id="symbol">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AAPL">Apple Inc. (AAPL)</SelectItem>
                          <SelectItem value="MSFT">Microsoft Corp. (MSFT)</SelectItem>
                          <SelectItem value="GOOGL">Alphabet Inc. (GOOGL)</SelectItem>
                          <SelectItem value="TSLA">Tesla Inc. (TSLA)</SelectItem>
                          <SelectItem value="AMZN">Amazon.com Inc. (AMZN)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <label htmlFor="timeframe-select" className="text-right text-sm font-medium">
                      Time Frame
                    </label>
                    <div className="col-span-3">
                      <Select value={timeframe} onValueChange={handleTimeframeChange}>
                        <SelectTrigger id="timeframe-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {timeframes.map((tf) => (
                            <SelectItem key={tf.value} value={tf.value}>
                              {tf.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 items-center gap-4">
                    <label htmlFor="volume" className="text-right text-sm font-medium">
                      Volume
                    </label>
                    <div className="col-span-3">
                      <Button
                        id="volume"
                        variant={showVolume ? "default" : "outline"}
                        size="sm"
                        onClick={() => setShowVolume(!showVolume)}
                        className="w-full"
                      >
                        {showVolume ? 'Volume Enabled' : 'Volume Disabled'}
                      </Button>
                    </div>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {isPopout && onClose && (
              <Button variant="ghost" size="icon" onClick={onClose} className="h-7 w-7">
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Applied Indicators */}
        {indicators.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {indicators.map((indicator, index) => (
              <div
                key={index}
                className="flex items-center gap-1 px-2 py-0.5 bg-secondary/50 rounded text-[11px]"
              >
                <div
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: indicator.color }}
                />
                <span>{indicator.name}({Object.values(indicator.parameters).join(', ')})</span>
                <button
                  onClick={() => removeIndicator(index)}
                  className="ml-0.5 hover:text-destructive text-muted-foreground"
                >
                  <X className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </CardHeader>

      <CardContent className="p-0">
        {/* These stand in for the chart, so they are the chart's height. At a fixed
            h-64 the box jumped the moment the first bar arrived - 256px to 400px on
            the dashboard, 256px down to 195px in the symbol drawer - which is what
            made the chart appear to pop and re-zoom on load. */}
        {error ? (
          <div
            className="flex items-center justify-center text-destructive text-sm"
            style={{ height: chartHeight }}
          >
            {error}
          </div>
        ) : bars.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center text-muted-foreground text-sm gap-2"
            style={{ height: chartHeight }}
          >
            <div className="h-6 w-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            <span>Loading {symbol} {timeframe}...</span>
          </div>
        ) : (
          <TradingViewChart
            data={bars}
            symbol={symbol}
            height={chartHeight}
            showVolume={showVolume}
            indicators={indicators}
            serverIndicators={serverIndicators}
            onCrosshairMove={handleCrosshairMove}
            onLoadOlder={loadOlderBars}
            pagingResetKey={`${symbol}_${timeframe}`}
            className="px-2 pb-2"
          />
        )}
      </CardContent>
    </Card>
  );
};

export default ModernChart;
